import { DotsThree, PencilSimpleLine, Trash } from "@phosphor-icons/react";
import { useRef } from "react";

import RenderIf from "common/components/RenderIf";
import { Button } from "common/components/ui/Button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuList,
  DropdownMenuTrigger,
} from "common/components/ui/DropdownMenu";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";

import { useActiveConversationContext } from "../../../context/ActiveConversationContext";
import useConversationMessagesUpdates from "../../../datahooks/useConversationMessagesUpdates";

import type { ConversationMessage } from "../../../types";

interface MessageMenuProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  accountId: string;
  message: ConversationMessage;
  setEditMessage: (message: ConversationMessage) => void;
}

export default function MessageMenu({
  isOpen,
  setIsOpen,
  accountId,
  message,
  setEditMessage,
}: MessageMenuProps) {
  const isDesktop = useTwBreakpoint("lg");
  const { activeConversation } = useActiveConversationContext();
  const shouldFocusInputRef = useRef(false);

  const { deleteMessage } = useConversationMessagesUpdates({
    conversationUrn: activeConversation.conversation_urn,
  });

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger className="relative" asChild>
        <Button
          intent="iconOnly"
          size={isDesktop ? "sm" : "xs"}
          variant="ghost-black"
        >
          <DotsThree />
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        onCloseAutoFocus={(e) => {
          if (shouldFocusInputRef.current) {
            document.getElementById("inbox-text-area").focus();
            e.preventDefault();
            shouldFocusInputRef.current = false;
          }
        }}
      >
        <DropdownMenuList>
          <RenderIf condition={!!message.body}>
            <DropdownMenuItem
              onSelect={() => {
                setEditMessage(message);
                shouldFocusInputRef.current = true;
              }}
            >
              <PencilSimpleLine />
              Edit message
            </DropdownMenuItem>
          </RenderIf>

          <DropdownMenuItem
            variant="danger"
            onSelect={() =>
              deleteMessage({ accountId, messageUrn: message.urn })
            }
          >
            <Trash />
            Delete message
          </DropdownMenuItem>
        </DropdownMenuList>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
